import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import type Hls from "hls.js";
import { NendaSymbol } from "./NendaSymbol";
import {
  formatTimecode,
  type ShowcaseChapter,
  type ShowcasePublicVideo,
} from "@shared/showcase";

/**
 * The player shell: a bare `<video>` with our own controls on top.
 *
 * Playback: native HLS where the browser has it (Safari, iOS - also the only
 * path that gets AirPlay and the system PiP for free), hls.js everywhere else,
 * loaded on first play so a cold open from an email paints the poster
 * instantly. hls.js runs WITHOUT its worker: the CSP has no `worker-src`
 * (see src/shared/csp.ts, same as the CMS players).
 */

export interface PlayerHandle {
  seek(seconds: number): void;
  play(): void;
}

interface Props {
  video: ShowcasePublicVideo;
  /** The playhead, ~4×/s while playing. */
  onTick?: (seconds: number, playing: boolean) => void;
  onFirstPlay?: () => void;
  onSeeked?: () => void;
  onEnded?: () => void;
  /** Rendered over the picture when the video has ended. */
  endCard?: React.ReactNode;
  compact?: boolean;
  /** The caption language on screen (null = off), for the transcript. */
  onCaptionLang?: (lang: string | null) => void;
}

const SPEEDS = [1, 1.25, 1.5, 2];

function chapterAt(
  chapters: ShowcaseChapter[],
  t: number
): ShowcaseChapter | null {
  let hit: ShowcaseChapter | null = null;
  for (const c of chapters) if (c.startSeconds <= t) hit = c;
  return hit;
}

export const Player = forwardRef<PlayerHandle, Props>(function Player(
  {
    video,
    onTick,
    onFirstPlay,
    onSeeked,
    onEnded,
    endCard,
    compact,
    onCaptionLang,
  },
  ref
) {
  const el = useRef<HTMLVideoElement>(null);
  const shell = useRef<HTMLDivElement>(null);
  const hls = useRef<Hls | null>(null);
  const attached = useRef(false);
  const started = useRef(false);
  const idleTimer = useRef<number | null>(null);

  const [hasStarted, setHasStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(video.durationSeconds ?? 0);
  const [buffered, setBuffered] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [speed, setSpeed] = useState(1);
  const [captionLang, setCaptionLang] = useState<string | null>(null);
  const [menu, setMenu] = useState<null | "speed" | "captions">(null);
  const [idle, setIdle] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [hover, setHover] = useState<{ x: number; t: number } | null>(null);
  const [failed, setFailed] = useState(false);
  const [quality, setQuality] = useState<string | null>(null);

  const chapters = video.chapters;
  const aspect =
    video.width && video.height ? `${video.width} / ${video.height}` : "16 / 9";

  /** Attach the stream once, on first play. */
  const attach = useCallback(async () => {
    const v = el.current;
    if (!v || attached.current) return;
    attached.current = true;
    if (v.canPlayType("application/vnd.apple.mpegurl")) {
      v.src = video.hlsUrl;
      return;
    }
    const { default: HlsCtor } = await import("hls.js");
    if (!HlsCtor.isSupported()) {
      v.src = video.hlsUrl; // let the browser try, and fail visibly
      return;
    }
    const h = new HlsCtor({
      enableWorker: false,
      capLevelToPlayerSize: true,
      startLevel: -1,
    });
    h.on(HlsCtor.Events.LEVEL_SWITCHED, (_e, data) => {
      const level = h.levels[data.level];
      setQuality(level?.height ? `${level.height}p` : null);
    });
    h.on(HlsCtor.Events.ERROR, (_e, data) => {
      if (!data.fatal) return;
      if (data.type === HlsCtor.ErrorTypes.NETWORK_ERROR) h.startLoad();
      else if (data.type === HlsCtor.ErrorTypes.MEDIA_ERROR)
        h.recoverMediaError();
      else setFailed(true);
    });
    h.loadSource(video.hlsUrl);
    h.attachMedia(v);
    hls.current = h;
  }, [video.hlsUrl]);

  useEffect(
    () => () => {
      hls.current?.destroy();
      hls.current = null;
    },
    []
  );

  const play = useCallback(async () => {
    const v = el.current;
    if (!v) return;
    await attach();
    if (!started.current) {
      started.current = true;
      setHasStarted(true);
      onFirstPlay?.();
    }
    setEnded(false);
    try {
      await v.play();
    } catch {
      // Autoplay policy: the user gesture path below will retry.
    }
  }, [attach, onFirstPlay]);

  const toggle = useCallback(() => {
    const v = el.current;
    if (!v) return;
    if (v.paused || v.ended) void play();
    else v.pause();
  }, [play]);

  const seek = useCallback(
    (t: number) => {
      const v = el.current;
      if (!v) return;
      const d =
        Number.isFinite(v.duration) && v.duration > 0 ? v.duration : duration;
      const target = Math.max(0, Math.min(t, d || t));
      void attach().then(() => {
        v.currentTime = target;
        setTime(target);
        if (v.paused) void play();
      });
    },
    [attach, duration, play]
  );

  useImperativeHandle(ref, () => ({ seek, play: () => void play() }), [
    seek,
    play,
  ]);

  // Media element → state.
  useEffect(() => {
    const v = el.current;
    if (!v) return;
    const on = (name: string, fn: () => void) => {
      v.addEventListener(name, fn);
      return () => v.removeEventListener(name, fn);
    };
    const offs = [
      on("play", () => setPlaying(true)),
      on("pause", () => setPlaying(false)),
      on("waiting", () => setWaiting(true)),
      // Playback actually running settles any earlier error: Safari fires a
      // transient `error` while its native HLS stack spins up, then plays.
      on("playing", () => {
        setWaiting(false);
        setFailed(false);
      }),
      on("canplay", () => {
        setWaiting(false);
        setFailed(false);
      }),
      on("timeupdate", () => {
        setTime(v.currentTime);
        onTick?.(v.currentTime, !v.paused);
      }),
      on("durationchange", () => {
        if (Number.isFinite(v.duration) && v.duration > 0)
          setDuration(v.duration);
      }),
      on("progress", () => {
        const b = v.buffered;
        if (b.length) setBuffered(b.end(b.length - 1));
      }),
      on("seeked", () => onSeeked?.()),
      on("volumechange", () => {
        setMuted(v.muted);
        setVolume(v.volume);
      }),
      on("ended", () => {
        setEnded(true);
        setPlaying(false);
        onEnded?.();
      }),
      on("error", () => {
        // Only a real media error counts: no `error` object, or an ABORTED
        // load (code 1, e.g. a source swap), is not a failure to play.
        const err = v.error;
        if (!err || err.code === 1) return;
        setFailed(true);
      }),
    ];
    return () => offs.forEach((off) => off());
  }, [onTick, onSeeked, onEnded]);

  // Captions: exactly one showing track, or none.
  useEffect(() => {
    const v = el.current;
    if (!v) return;
    for (const track of Array.from(v.textTracks)) {
      track.mode = track.language === captionLang ? "showing" : "disabled";
    }
    onCaptionLang?.(captionLang);
  }, [captionLang, onCaptionLang]);

  useEffect(() => {
    if (el.current) el.current.playbackRate = speed;
  }, [speed]);

  // Lock screen / headphones controls.
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: video.title,
      artist: "Nenda",
      artwork: video.posterUrl
        ? [{ src: video.posterUrl, sizes: "1280x720", type: "image/jpeg" }]
        : [],
    });
    navigator.mediaSession.setActionHandler("play", () => void play());
    navigator.mediaSession.setActionHandler("pause", () => el.current?.pause());
    navigator.mediaSession.setActionHandler("seekto", (d) => {
      if (d.seekTime !== undefined) seek(d.seekTime);
    });
  }, [video.title, video.posterUrl, play, seek]);

  // Controls fade away while playing and the pointer rests.
  const wake = useCallback(() => {
    setIdle(false);
    if (idleTimer.current) window.clearTimeout(idleTimer.current);
    idleTimer.current = window.setTimeout(() => setIdle(true), 2600);
  }, []);
  useEffect(
    () => () => {
      if (idleTimer.current) window.clearTimeout(idleTimer.current);
    },
    []
  );

  const fullscreen = useCallback(() => {
    const s = shell.current;
    const v = el.current as
      | (HTMLVideoElement & { webkitEnterFullscreen?: () => void })
      | null;
    if (!s) return;
    if (document.fullscreenElement) void document.exitFullscreen();
    else if (s.requestFullscreen) void s.requestFullscreen();
    else v?.webkitEnterFullscreen?.(); // iOS
  }, []);

  const pip = useCallback(async () => {
    const v = el.current;
    if (!v) return;
    try {
      if (document.pictureInPictureElement)
        await document.exitPictureInPicture();
      else await v.requestPictureInPicture();
    } catch {
      // not supported here
    }
  }, []);

  // Keyboard: YouTube's map, which is what people's fingers already know.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.isContentEditable)
      )
        return;
      const v = el.current;
      if (!v) return;
      switch (e.key) {
        case " ":
        case "k":
          e.preventDefault();
          toggle();
          break;
        case "ArrowLeft":
          e.preventDefault();
          seek(v.currentTime - 5);
          break;
        case "ArrowRight":
          e.preventDefault();
          seek(v.currentTime + 5);
          break;
        case "j":
          seek(v.currentTime - 10);
          break;
        case "l":
          seek(v.currentTime + 10);
          break;
        case "f":
          fullscreen();
          break;
        case "m":
          v.muted = !v.muted;
          break;
        case "c":
          if (video.captions.length)
            setCaptionLang((cur) => (cur ? null : video.captions[0].lang));
          break;
        default:
          return;
      }
      wake();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggle, seek, fullscreen, wake, video.captions]);

  const pct = (t: number) =>
    duration > 0 ? Math.min(100, (t / duration) * 100) : 0;
  const current = chapterAt(chapters, time);
  const hoverChapter = hover ? chapterAt(chapters, hover.t) : null;

  const scrubTo = (e: React.PointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - r.left, r.width));
    return { x, t: r.width > 0 ? (x / r.width) * duration : 0 };
  };

  const showChrome = !playing || !idle || menu !== null;

  const tracks = useMemo(
    () =>
      video.captions.map((c) => (
        <track
          key={c.lang}
          kind="subtitles"
          src={c.url}
          srcLang={c.lang}
          label={c.label}
        />
      )),
    [video.captions]
  );

  return (
    <div
      ref={shell}
      className={`player${showChrome ? "" : " player--idle"}${compact ? " player--compact" : ""}`}
      style={{ aspectRatio: aspect }}
      onPointerMove={wake}
      onPointerLeave={() => playing && setIdle(true)}
    >
      <video
        ref={el}
        className="player__video"
        poster={video.posterUrl ?? undefined}
        playsInline
        crossOrigin="anonymous"
        preload="none"
        onClick={toggle}
        onDoubleClick={fullscreen}
      >
        {tracks}
      </video>

      {!hasStarted && (
        <button
          className="player__start"
          onClick={() => void play()}
          aria-label="Play video"
        >
          {/* The intro: the Nenda symbol draws itself, spins away, and the
              play triangle springs in (styles.css, "Start intro"). Playable
              from the first frame - the button never waits for it. */}
          <span className="player__start-ring">
            <NendaSymbol className="player__start-symbol" draw />
            <svg
              className="player__start-play"
              viewBox="0 0 24 24"
              width="34"
              height="34"
              aria-hidden="true"
            >
              <path d="M8 5.5v13l10.5-6.5z" fill="currentColor" />
            </svg>
          </span>
          {video.durationSeconds ? (
            <span className="player__start-length">
              {formatTimecode(video.durationSeconds)}
            </span>
          ) : null}
        </button>
      )}

      {waiting && playing && (
        <div className="player__spinner" aria-label="Loading" />
      )}

      {failed && (
        <div className="player__failed">
          This video could not be played on this device. Try another browser, or
          reload the page.
        </div>
      )}

      {ended && endCard && <div className="player__endcard">{endCard}</div>}

      {hasStarted && (
        <div className="player__chrome" onClick={(e) => e.stopPropagation()}>
          {current && !compact && (
            <div className="player__chapter-now">{current.title}</div>
          )}
          <div
            className="scrub"
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={Math.floor(duration)}
            aria-valuenow={Math.floor(time)}
            aria-valuetext={formatTimecode(time)}
            tabIndex={0}
            onPointerMove={(e) => setHover(scrubTo(e))}
            onPointerLeave={() => setHover(null)}
            onPointerDown={(e) => {
              e.currentTarget.setPointerCapture(e.pointerId);
              seek(scrubTo(e).t);
            }}
          >
            <div className="scrub__track">
              <div
                className="scrub__buffered"
                style={{ width: `${pct(buffered)}%` }}
              />
              <div
                className="scrub__played"
                style={{ width: `${pct(time)}%` }}
              />
              {chapters
                .filter((c) => c.startSeconds > 0)
                .map((c) => (
                  <span
                    key={c.startSeconds}
                    className="scrub__tick"
                    style={{ left: `${pct(c.startSeconds)}%` }}
                  />
                ))}
            </div>
            <div className="scrub__knob" style={{ left: `${pct(time)}%` }} />
            {hover && (
              <div className="scrub__hover" style={{ left: hover.x }}>
                {hoverChapter && (
                  <span className="scrub__hover-title">
                    {hoverChapter.title}
                  </span>
                )}
                <span>{formatTimecode(hover.t)}</span>
              </div>
            )}
          </div>

          <div className="bar">
            <button
              className="icon"
              onClick={toggle}
              aria-label={playing ? "Pause" : "Play"}
            >
              {playing ? (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z"
                    fill="currentColor"
                  />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M8 5.5v13l10.5-6.5z" fill="currentColor" />
                </svg>
              )}
            </button>
            <button
              className="icon"
              onClick={() => seek(time - 10)}
              aria-label="Back 10 seconds"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M12 5V2L7 6l5 4V7a6 6 0 1 1-6 6H4a8 8 0 1 0 8-8z"
                  fill="currentColor"
                />
              </svg>
            </button>
            <div className="volume">
              <button
                className="icon"
                onClick={() => {
                  if (el.current) el.current.muted = !el.current.muted;
                }}
                aria-label={muted ? "Unmute" : "Mute"}
              >
                {muted || volume === 0 ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M4 9v6h4l5 4V5L8 9zm12.6 3 2.7-2.7-1.3-1.3-2.7 2.7-2.7-2.7-1.3 1.3 2.7 2.7-2.7 2.7 1.3 1.3 2.7-2.7 2.7 2.7 1.3-1.3z"
                      fill="currentColor"
                    />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M4 9v6h4l5 4V5L8 9zm11.5 3a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4zM13 3.2v2.1a7 7 0 0 1 0 13.4v2.1a9 9 0 0 0 0-17.6z"
                      fill="currentColor"
                    />
                  </svg>
                )}
              </button>
              <input
                className="volume__slider"
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={muted ? 0 : volume}
                aria-label="Volume"
                onChange={(e) => {
                  const v = el.current;
                  if (!v) return;
                  v.volume = Number(e.target.value);
                  v.muted = v.volume === 0;
                }}
              />
            </div>
            <span className="time">
              {formatTimecode(time)}{" "}
              <span className="time__total">/ {formatTimecode(duration)}</span>
            </span>
            <span className="bar__spacer" />
            {quality && !compact && <span className="badge">{quality}</span>}
            {video.captions.length > 0 && (
              <div className="menu-anchor">
                <button
                  className={`icon${captionLang ? " icon--on" : ""}`}
                  onClick={() =>
                    setMenu(menu === "captions" ? null : "captions")
                  }
                  aria-label="Captions"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1zm2.5 9.5h4V13h-4zm5.5 0h5.5V13H12zm-5.5-3h7V10h-7zm8.5 0h2.5V10H15z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
                {menu === "captions" && (
                  <div className="menu" role="menu">
                    <button
                      className={!captionLang ? "is-on" : ""}
                      onClick={() => {
                        setCaptionLang(null);
                        setMenu(null);
                      }}
                    >
                      Off
                    </button>
                    {video.captions.map((c) => (
                      <button
                        key={c.lang}
                        className={captionLang === c.lang ? "is-on" : ""}
                        onClick={() => {
                          setCaptionLang(c.lang);
                          setMenu(null);
                        }}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="menu-anchor">
              <button
                className="icon icon--text"
                onClick={() => setMenu(menu === "speed" ? null : "speed")}
                aria-label="Playback speed"
              >
                {speed}×
              </button>
              {menu === "speed" && (
                <div className="menu" role="menu">
                  {SPEEDS.map((s) => (
                    <button
                      key={s}
                      className={speed === s ? "is-on" : ""}
                      onClick={() => {
                        setSpeed(s);
                        setMenu(null);
                      }}
                    >
                      {s === 1 ? "Normal" : `${s}×`}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {"pictureInPictureEnabled" in document &&
              document.pictureInPictureEnabled && (
                <button
                  className="icon"
                  onClick={() => void pip()}
                  aria-label="Picture in picture"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M3 5h18v14H3zm2 2v10h14V7zm7 4h6v5h-6z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              )}
            <button
              className="icon"
              onClick={fullscreen}
              aria-label="Full screen"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M4 4h6v2H6v4H4zm10 0h6v6h-2V6h-4zM4 14h2v4h4v2H4zm14 0h2v6h-6v-2h4z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
});
